import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogTitleProps,
  FormControl,
  FormControlProps,
  InputAdornment,
  SxProps,
  TextFieldProps,
} from '@mui/material';

import { DateRange as DateRangeIcon } from '@mui/icons-material';

import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
} from '@mui/lab';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { useAppSelector } from '../../../../../hooks';
import { getAuthState } from '../../../../../store/AuthSlice';
import { TP_COLORS } from '../../../../../constants';
import { TPTextField } from '../../../../../components/Inputs';
import {
  addTutorQualification,
  updateTutorQualification,
} from '../../../../../store/UserSlice/UserApi';
import { IUserQualification } from '../../../../../store/UserSlice';
import { addButtonProps, dismissButtonProps } from '..';
import {
  dialogTitleProps,
  formControlProps,
  textFieldProps,
} from './AddExperienceModal';

type TState = {
  qualification: string;
  institute: string;
  startDate: Moment | null;
  endDate: Moment | null;
};

const initialState: TState = {
  qualification: '',
  institute: '',
  startDate: null,
  endDate: null,
};

interface AddQualificationModalProps {
  open: boolean;
  selectedQualificationItem?: IUserQualification;
  onClose: () => void;
}

const AddQualificationModal: FunctionComponent<AddQualificationModalProps> = ({
  open,
  selectedQualificationItem,
  onClose,
}) => {
  const dispatch = useDispatch();
  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);
  const [mode, setMode] = useState<'add' | 'edit'>('add');

  const isSubmitDisabled =
    !state.qualification.trim().length ||
    !state.institute.trim().length ||
    !state.startDate ||
    !state.endDate ||
    authState.status === 'loading';

  useEffect(() => {
    if (selectedQualificationItem) {
      setMode('edit');
      setState({
        qualification: selectedQualificationItem.qualification,
        institute: selectedQualificationItem.institute,
        startDate: moment(selectedQualificationItem.startDate),
        endDate: moment(selectedQualificationItem.endDate),
      });
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleStartDateChange = (date: Moment | null) => {
    setState((prevState) => ({
      ...prevState,
      startDate: date,
    }));
  };

  const handleEndDateChange = (date: Moment | null) => {
    setState((prevState) => ({
      ...prevState,
      endDate: date,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (mode === 'edit' && selectedQualificationItem) {
      await dispatch(
        updateTutorQualification({
          ...state,
          _id: selectedQualificationItem._id,
          startDate: state.startDate?.toISOString() || '',
          endDate: state.endDate?.toISOString() || '',
        })
      );
    } else {
      await dispatch(
        addTutorQualification({
          ...state,
          startDate: state.startDate?.toISOString() || '',
          endDate: state.endDate?.toISOString() || '',
        })
      );
    }
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { padding: '30px', borderRadius: '20px' } }}
    >
      <DialogTitle {...dialogTitleProps}>
        {mode === 'edit' ? 'Update' : 'Add'} Qualification
      </DialogTitle>
      <DialogContentText fontSize="12px">
        List your educational qualifications and certifications.
      </DialogContentText>
      <DialogContent sx={{ padding: '30px 0px', maxWidth: '380px' }}>
        <FormControl {...formControlProps}>
          <TPTextField
            {...textFieldProps}
            name="qualification"
            label="Qualification*"
            placeholder="Qualification"
            value={state.qualification}
            onChange={handleInputChange}
          />
        </FormControl>
        <FormControl {...formControlProps}>
          <TPTextField
            {...textFieldProps}
            name="institute"
            label="Institute*"
            placeholder="Institute"
            value={state.institute}
            onChange={handleInputChange}
          />
        </FormControl>
        <FormControl {...formControlProps}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <MobileDatePicker
              views={['year']}
              disableCloseOnSelect={false}
              value={state.startDate}
              onChange={handleStartDateChange}
              label="Start Year*"
              maxDate={moment()}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  placeholder="Start Year"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </FormControl>
        <FormControl {...formControlProps}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <MobileDatePicker
              views={['year']}
              disableCloseOnSelect={false}
              value={state.endDate}
              onChange={handleEndDateChange}
              label="End Year*"
              maxDate={moment()}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  placeholder="End Year"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ padding: '10px 0 0 0' }}>
        <Button {...dismissButtonProps} onClick={onClose}>
          Dismiss
        </Button>
        <LoadingButton
          {...addButtonProps}
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddQualificationModal;
